import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import {
  AlertTriangle,
  Loader2,
  Pencil,
  Plus,
  Trash2,
} from 'lucide-react'
import type { StatusDocument } from '@/lib/db/rxdb/schemas/status'
import { useStatuses } from '@/routes/features/tasks/hooks/useWorkspaceStatuses'
import { useStatusMutations } from '@/lib/db/rxdb/hooks'
import {
  attachStatusToProject,
  detachStatusFromProject,
} from '@/lib/api/features/project-statuses'
import { StatusFormDialog } from '@/routes/features/settings/status/components/StatusFormDialog'
import { PROGRESS_TYPES, PROGRESS_TYPE_ORDER } from '@/lib/types/subscription'
import { useHasPermission } from '@/lib/hooks/useWorkspaceRole'
import { WORKSPACE_PERMISSIONS } from '@/lib/types/roles'
import useLoginStore from '@/store/LoginStore'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Separator } from '@/components/ui/separator'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { logger } from '@/lib/monitoring/logger'
import { LogCategory } from '@/lib/monitoring/types'
import { StatusIcon } from '@/components/app/indicators/StatusIcon'

interface ProjectStatusesSectionProps {
  workspaceSlug: string
  projectId: string
  projectStatusMode?: 'simple' | 'advanced' | null
  statusMigrationInProgress?: boolean
}

const SIMPLE_MODE_ALLOWED_TYPES: ReadonlyArray<string> = [
  PROGRESS_TYPES.BACKLOG,
  PROGRESS_TYPES.COMPLETED,
]

export function ProjectStatusesSection({
  workspaceSlug,
  projectId,
  projectStatusMode,
  statusMigrationInProgress = false,
}: ProjectStatusesSectionProps) {
  const { t } = useTranslation('settings')
  const canManage = useHasPermission(
    WORKSPACE_PERMISSIONS.MANAGE_WORKSPACE_SETTINGS,
  )
  const workspaceId = useLoginStore((state) => state.workspaceId)

  const { all, isLoading } = useStatuses()
  const { deleteStatus } = useStatusMutations()

  const [pendingStatusId, setPendingStatusId] = useState<string | null>(null)
  const [isAttachPopoverOpen, setIsAttachPopoverOpen] = useState(false)
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false)
  const [editingStatus, setEditingStatus] = useState<StatusDocument | null>(
    null,
  )
  const [deleteTarget, setDeleteTarget] = useState<StatusDocument | null>(null)

  const statuses = all.statuses
  const isSimpleMode = projectStatusMode === 'simple'

  // Statuses currently visible in this project
  const visibleStatuses = useMemo<Array<StatusDocument>>(() => {
    return statuses
      .filter((status) => {
        const inScope =
          status.projectIds.length === 0 ||
          status.projectIds.includes(projectId)
        if (!inScope) return false
        if (isSimpleMode) {
          return SIMPLE_MODE_ALLOWED_TYPES.includes(status.progressType)
        }
        return true
      })
      .sort((a, b) => a.sortOrder.localeCompare(b.sortOrder))
  }, [statuses, projectId, isSimpleMode])

  // Group visible statuses by progressType, in canonical workflow order.
  // Within each group, the parent sort by sortOrder is preserved.
  const groupedStatuses = useMemo(() => {
    const known = PROGRESS_TYPE_ORDER.map((pt) => ({
      progressType: pt as string,
      statuses: visibleStatuses.filter((s) => s.progressType === pt),
    }))
    const knownTypes = PROGRESS_TYPE_ORDER as ReadonlyArray<string>
    const unknownTypes = Array.from(
      new Set(
        visibleStatuses
          .filter((s) => !knownTypes.includes(s.progressType))
          .map((s) => s.progressType),
      ),
    )
    const unknown = unknownTypes.map((pt) => ({
      progressType: pt,
      statuses: visibleStatuses.filter((s) => s.progressType === pt),
    }))
    return [...known, ...unknown].filter((g) => g.statuses.length > 0)
  }, [visibleStatuses])

  // Statuses that can be attached (not already visible, not system-generated)
  const attachableStatuses = useMemo<Array<StatusDocument>>(() => {
    const visibleIds = new Set(visibleStatuses.map((s) => s.id))
    return statuses
      .filter((status) => {
        if (visibleIds.has(status.id)) return false
        if (status.isSystemGenerated) return false
        if (
          isSimpleMode &&
          !SIMPLE_MODE_ALLOWED_TYPES.includes(status.progressType)
        ) {
          return false
        }
        return true
      })
      .sort((a, b) => a.name.localeCompare(b.name))
  }, [statuses, visibleStatuses, isSimpleMode])

  const migrationInProgress = statusMigrationInProgress

  const mutationsDisabled =
    !canManage || pendingStatusId !== null || migrationInProgress

  const runMutation = async (
    statusId: string,
    action: () => Promise<void>,
  ) => {
    if (!canManage) return
    setPendingStatusId(statusId)
    try {
      await action()
    } finally {
      setPendingStatusId(null)
    }
  }

  const handleAttach = (status: StatusDocument) => {
    void runMutation(status.id, async () => {
      try {
        const response = await attachStatusToProject(
          workspaceSlug,
          projectId,
          status.id,
        )
        setIsAttachPopoverOpen(false)

        if (!response.changed) {
          toast.info(
            t('project_statuses.toast.attachNoop', { name: status.name }),
          )
          return
        }
        toast.success(
          t('project_statuses.toast.attachSuccess', { name: status.name }),
        )
        logger.logEvent(LogCategory.STATE, 'project_status_attached', {
          component: 'ProjectStatusesSection',
          projectId,
          statusId: status.id,
        })
      } catch (error) {
        logger.logError(error as Error, LogCategory.API_ERROR, {
          component: 'ProjectStatusesSection',
          action: 'attach_status',
          projectId,
          statusId: status.id,
        })
        toast.error(
          error instanceof Error
            ? error.message
            : t('project_statuses.toast.attachError'),
        )
      }
    })
  }

  const handleDeleteWorkspace = (status: StatusDocument) => {
    void runMutation(status.id, async () => {
      try {
        await deleteStatus(status.id)
        setDeleteTarget(null)
        toast.success(
          t('project_statuses.toast.deleteSuccess', { name: status.name }),
        )
        logger.logEvent(LogCategory.STATE, 'project_status_deleted', {
          component: 'ProjectStatusesSection',
          projectId,
          statusId: status.id,
        })
      } catch (error) {
        logger.logError(error as Error, LogCategory.API_ERROR, {
          component: 'ProjectStatusesSection',
          action: 'delete_status',
          projectId,
          statusId: status.id,
        })
        toast.error(
          error instanceof Error
            ? error.message
            : t('project_statuses.toast.deleteError'),
        )
      }
    })
  }

  const handleDetach = (status: StatusDocument) => {
    void runMutation(status.id, async () => {
      const wasGlobal = status.projectIds.length === 0
      try {
        const response = await detachStatusFromProject(
          workspaceSlug,
          projectId,
          status.id,
        )
        if (!response.changed) {
          toast.info(
            t('project_statuses.toast.detachNoop', { name: status.name }),
          )
          return
        }
        if (wasGlobal && typeof response.expandedToProjects === 'number') {
          toast.success(
            t('project_statuses.toast.detachGlobalExpanded', {
              name: status.name,
              count: response.expandedToProjects,
            }),
          )
        } else {
          toast.success(
            t('project_statuses.toast.detachSuccess', { name: status.name }),
          )
        }
        logger.logEvent(LogCategory.STATE, 'project_status_detached', {
          component: 'ProjectStatusesSection',
          projectId,
          statusId: status.id,
        })
      } catch (error) {
        logger.logError(error as Error, LogCategory.API_ERROR, {
          component: 'ProjectStatusesSection',
          action: 'detach_status',
          projectId,
          statusId: status.id,
        })
        toast.error(
          error instanceof Error
            ? error.message
            : t('project_statuses.toast.detachError'),
        )
      }
    })
  }

  return (
    <>
      <Separator className="my-8" />
      <div>
        <p className="text-sm font-medium mb-1">
          {t('project_statuses.title')}
        </p>
        <p className="text-xs text-muted-foreground mb-4">
          {t('project_statuses.description')}
        </p>

        {migrationInProgress && (
          <div className="mb-4 flex items-start gap-2 rounded-md border border-amber-500/30 bg-amber-500/10 p-3">
            <AlertTriangle className="size-4 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
            <p className="text-xs text-amber-900 dark:text-amber-200">
              {t('project_statuses.migrationBanner')}
            </p>
          </div>
        )}

        <div className="rounded-lg border bg-card">
          {isLoading ? (
            <div className="flex items-center justify-center p-6">
              <Loader2 className="size-4 animate-spin text-muted-foreground" />
            </div>
          ) : visibleStatuses.length === 0 ? (
            <div className="p-6 text-center">
              <p className="text-sm text-muted-foreground">
                {t('project_statuses.empty')}
              </p>
            </div>
          ) : (
            <div className="divide-y">
              {groupedStatuses.map((group) => (
                <div key={group.progressType}>
                  <div className="flex items-center gap-2 bg-muted/40 px-4 py-2 border-b">
                    <StatusIcon progressType={group.progressType} size="sm" />
                    <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                      {t(
                        `project_statuses.progressType.${group.progressType}`,
                        { defaultValue: group.progressType },
                      )}
                    </span>
                    <Badge
                      variant="outline"
                      className="ml-1 px-1.5 py-0 text-[10px] font-normal"
                    >
                      {group.statuses.length}
                    </Badge>
                  </div>
                  <ul className="divide-y">
                    {group.statuses.map((status) => {
                      const isGlobal = status.projectIds.length === 0
                      const isSystem = status.isSystemGenerated
                      const isPending = pendingStatusId === status.id
                      const canRemove =
                        !isSystem && canManage && !migrationInProgress
                      const isSoleUser =
                        !isGlobal &&
                        status.projectIds.length === 1 &&
                        status.projectIds[0] === projectId
                      const removeAction = isSoleUser
                        ? () => setDeleteTarget(status)
                        : () => handleDetach(status)
                      const removeLabelKey = isSoleUser
                        ? 'project_statuses.actions.deleteWorkspace'
                        : 'project_statuses.actions.remove'
                      return (
                        <li
                          key={status.id}
                          className="flex items-center gap-3 px-4 py-3"
                        >
                          <StatusIcon progressType={status.progressType} statusId={status.id} size="sm" />
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-medium truncate">
                              {status.name}
                            </p>
                            {status.description && (
                              <p className="text-xs text-muted-foreground truncate">
                                {status.description}
                              </p>
                            )}
                          </div>
                          <Badge
                            variant={isGlobal ? 'secondary' : 'outline'}
                            className="text-xs shrink-0"
                          >
                            {isGlobal
                              ? t('project_statuses.scope.global')
                              : t('project_statuses.scope.projectScoped')}
                          </Badge>
                          {isSystem ? (
                            <Badge
                              variant="outline"
                              className="text-xs shrink-0"
                            >
                              {t('project_statuses.scope.system')}
                            </Badge>
                          ) : (
                            <TooltipProvider delayDuration={200}>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <span>
                                    <Button
                                      type="button"
                                      variant="ghost"
                                      size="icon"
                                      className="h-8 w-8 shrink-0"
                                      disabled={
                                        !canManage ||
                                        migrationInProgress ||
                                        isPending
                                      }
                                      onClick={() => setEditingStatus(status)}
                                      aria-label={t(
                                        'project_statuses.actions.edit',
                                      )}
                                    >
                                      <Pencil className="size-4" />
                                    </Button>
                                  </span>
                                </TooltipTrigger>
                                <TooltipContent>
                                  {canManage
                                    ? t('project_statuses.actions.edit')
                                    : t('project_statuses.noPermission')}
                                </TooltipContent>
                              </Tooltip>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <span>
                                    <Button
                                      type="button"
                                      variant="ghost"
                                      size="icon"
                                      className="h-8 w-8 shrink-0"
                                      disabled={!canRemove || isPending}
                                      onClick={removeAction}
                                      aria-label={t(removeLabelKey)}
                                    >
                                      {isPending ? (
                                        <Loader2 className="size-4 animate-spin" />
                                      ) : (
                                        <Trash2 className="size-4" />
                                      )}
                                    </Button>
                                  </span>
                                </TooltipTrigger>
                                <TooltipContent>
                                  {canManage
                                    ? t(removeLabelKey)
                                    : t('project_statuses.noPermission')}
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          )}
                        </li>
                      )
                    })}
                  </ul>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="mt-3 flex flex-wrap items-center gap-2">
          <Popover
            open={isAttachPopoverOpen}
            onOpenChange={(open) => {
              if (!canManage || migrationInProgress) return
              setIsAttachPopoverOpen(open)
            }}
          >
            <PopoverTrigger asChild>
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={mutationsDisabled || attachableStatuses.length === 0}
              >
                <Plus className="size-3.5" />
                {t('project_statuses.actions.addExisting')}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[320px] p-0" align="start">
              <Command>
                <CommandInput
                  placeholder={t('project_statuses.searchPlaceholder')}
                />
                <CommandList>
                  <CommandEmpty>
                    {t('project_statuses.noAttachable')}
                  </CommandEmpty>
                  <CommandGroup>
                    {attachableStatuses.map((status) => (
                      <CommandItem
                        key={status.id}
                        value={`${status.name} ${status.progressType}`}
                        onSelect={() => handleAttach(status)}
                        disabled={pendingStatusId === status.id}
                      >
                        <StatusIcon
                          progressType={status.progressType}
                          statusId={status.id}
                          size="sm"
                          className="mr-2"
                        />
                        <span className="flex-1 truncate">{status.name}</span>
                        {pendingStatusId === status.id && (
                          <Loader2 className="size-3.5 animate-spin" />
                        )}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>

          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={mutationsDisabled}
            onClick={() => setIsCreateDialogOpen(true)}
          >
            <Plus className="size-3.5" />
            {t('project_statuses.actions.createProjectOnly')}
          </Button>
          {!canManage && (
            <p className="text-xs text-muted-foreground">
              {t('project_statuses.noPermission')}
            </p>
          )}
        </div>
      </div>

      <StatusFormDialog
        open={isCreateDialogOpen}
        onOpenChange={setIsCreateDialogOpen}
        mode="create"
        workspaceId={workspaceId}
        initialProjectIds={[projectId]}
      />

      <StatusFormDialog
        open={editingStatus !== null}
        onOpenChange={(open) => {
          if (!open) setEditingStatus(null)
        }}
        mode="edit"
        workspaceId={workspaceId}
        initialStatus={editingStatus ?? undefined}
      />

      <AlertDialog
        open={deleteTarget !== null}
        onOpenChange={(open) => {
          if (!open && pendingStatusId === null) setDeleteTarget(null)
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t('project_statuses.deleteDialog.title')}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t('project_statuses.deleteDialog.description', {
                name: deleteTarget?.name ?? '',
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={pendingStatusId !== null}>
              {t('project_statuses.deleteDialog.cancel')}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault()
                if (deleteTarget) handleDeleteWorkspace(deleteTarget)
              }}
              disabled={pendingStatusId !== null}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {t('project_statuses.deleteDialog.confirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}

